import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../api';
import { canWrite, useAuth } from '../auth';
import { useLocations } from '../queries';
import { Badge, Button, Card, Empty, ErrorMessage, Field, Input, Notice, PageHeader, Select, Spinner, number, plural, useConfirm } from '../ui';

interface TransferRow {
  id: number;
  number: string;
  status: 'pending' | 'in_transit' | 'received' | 'cancelled';
  from: string;
  to: string;
  shippedOn: string | null;
  receivedOn: string | null;
  pieces: number;
  lines: number;
}
interface TransferDetailData {
  id: number;
  number: string;
  status: TransferRow['status'];
  from: string;
  to: string;
  notes: string | null;
  shippedOn: string | null;
  receivedOn: string | null;
  lines: { id: number; lotId: number; itemNo: string; itemName: string; colorName: string; condition: string; qty: number; qtyShipped: number; qtyReceived: number }[];
}
const TONE = { pending: 'slate', in_transit: 'blue', received: 'green', cancelled: 'slate' } as const;
const WORD = { pending: 'Not sent', in_transit: 'In transit', received: 'Received', cancelled: 'Cancelled' } as const;
const today = () => new Date().toISOString().slice(0, 10);

/** Transfers of stock between locations, with an in-transit stage so stock on the move is neither available nor lost. */
export function Transfers() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const list = useQuery({ queryKey: ['transfers'], queryFn: () => api<{ transfers: TransferRow[] }>('/api/v1/transfers').then((r) => r.transfers) });
  const locations = useLocations();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [notes, setNotes] = useState('');
  const [qty, setQty] = useState<Record<number, string>>({});
  const stock = useQuery({
    queryKey: ['location-stock', from],
    enabled: !!from,
    queryFn: () => api<{ rows: { lotId: number; itemNo: string; itemName: string; colorName: string; condition: string; qty: number }[] }>(`/api/v1/locations/${from}/stock`).then((r) => r.rows),
  });
  const lines = Object.entries(qty).filter(([, v]) => Number(v) > 0).map(([lotId, v]) => ({ lotId: Number(lotId), qty: Number(v) }));
  const create = useMutation({
    mutationFn: () => api<{ transfer: { id: number } }>('/api/v1/transfers', { body: { fromLocationId: Number(from), toLocationId: Number(to), notes: notes || null, lines } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['transfers'] });
      navigate(`/transfers/${r.transfer.id}`);
    },
  });
  return (
    <>
      <PageHeader title="Transfers" subtitle="Move stock between locations. While it is on its way it is in transit: not available to sell, but not lost." actions={writable && !adding ? <Button onClick={() => setAdding(true)}>New transfer</Button> : undefined} />
      <div className="space-y-4">
        {adding && (
          <Card title="New transfer">
            <div className="flex flex-wrap items-end gap-3">
              <Field label="From">
                <Select value={from} onChange={(e) => { setFrom(e.target.value); setQty({}); }}>
                  <option value="">Choose…</option>
                  {locations.data?.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="To">
                <Select value={to} onChange={(e) => setTo(e.target.value)}>
                  <option value="">Choose…</option>
                  {locations.data?.filter((l) => String(l.id) !== from).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Notes">
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. for the show" />
              </Field>
            </div>
            {from && stock.isPending && <Spinner />}
            {from && stock.data && stock.data.length === 0 && <Notice tone="amber">There is no good stock in that location.</Notice>}
            {stock.data && stock.data.length > 0 && (
              <div className="mt-3 max-h-96 overflow-auto">
                <table className="data w-full text-sm">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Colour</th>
                      <th>Cond.</th>
                      <th className="text-right">There</th>
                      <th className="text-right">Move</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stock.data.map((r) => (
                      <tr key={r.lotId}>
                        <td>
                          <span className="font-medium">{r.itemNo}</span>
                          <div className="max-w-64 truncate text-xs text-slate-500">{r.itemName}</div>
                        </td>
                        <td>{r.colorName}</td>
                        <td>{r.condition === 'N' ? 'New' : 'Used'}</td>
                        <td className="text-right tabular-nums">{number(r.qty)}</td>
                        <td className="text-right">
                          <Input aria-label={`Move ${r.itemNo} ${r.colorName}`} inputMode="numeric" className={`w-20 text-right ${Number(qty[r.lotId] ?? 0) > r.qty ? 'border-red-400' : ''}`} value={qty[r.lotId] ?? ''} onChange={(e) => setQty({ ...qty, [r.lotId]: e.target.value })} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <ErrorMessage error={create.error} />
            <div className="mt-3 flex gap-2">
              <Button disabled={create.isPending || !from || !to || lines.length === 0 || stock.data?.some((r) => Number(qty[r.lotId] ?? 0) > r.qty)} onClick={() => create.mutate()}>
                Create transfer ({plural(lines.reduce((t, l) => t + l.qty, 0), 'piece')})
              </Button>
              <Button variant="ghost" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </Card>
        )}
        <Card>
          {list.isPending && <Spinner />}
          {list.data && list.data.length === 0 && <Empty>No transfers yet.</Empty>}
          {list.data && list.data.length > 0 && (
            <table className="data w-full text-sm">
              <thead>
                <tr>
                  <th>Transfer</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Status</th>
                  <th className="text-right">Pieces</th>
                  <th>Sent</th>
                  <th>Received</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <Link to={`/transfers/${t.id}`} className="font-medium hover:underline">
                        {t.number}
                      </Link>
                    </td>
                    <td>{t.from}</td>
                    <td>{t.to}</td>
                    <td>
                      <Badge tone={TONE[t.status]}>{WORD[t.status]}</Badge>
                    </td>
                    <td className="text-right tabular-nums">{number(t.pieces)}</td>
                    <td>{t.shippedOn ?? '—'}</td>
                    <td>{t.receivedOn ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}

export function TransferDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const writable = canWrite(user);
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const q = useQuery({ queryKey: ['transfer', id], queryFn: () => api<{ transfer: TransferDetailData }>(`/api/v1/transfers/${id}`).then((r) => r.transfer) });
  const [got, setGot] = useState<Record<number, string>>({});
  const refresh = () => {
    for (const k of ['transfer', 'transfers', 'lots', 'lot', 'dashboard', 'location-stock']) void qc.invalidateQueries({ queryKey: [k] });
  };
  const act = useMutation({
    mutationFn: (v: { path: string; body?: unknown }) => api(`/api/v1/transfers/${id}/${v.path}`, { body: v.body ?? {} }),
    onSuccess: refresh,
  });
  if (q.isPending) return <Spinner />;
  if (q.error || !q.data) return <ErrorMessage error={q.error} />;
  const t = q.data;
  return (
    <>
      {dialog}
      <PageHeader
        title={t.number}
        subtitle={
          <>
            <Link to="/transfers" className="hover:underline">
              Transfers
            </Link>{' '}
            / {t.from} → {t.to}
            {t.notes ? ` · ${t.notes}` : ''}
          </>
        }
        actions={<Badge tone={TONE[t.status]}>{WORD[t.status]}</Badge>}
      />
      <Card title="Lines">
        <table className="data w-full text-sm">
          <thead>
            <tr>
              <th>Item</th>
              <th>Colour</th>
              <th>Cond.</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Sent</th>
              <th className="text-right">{t.status === 'in_transit' ? 'Arrived' : 'Received'}</th>
            </tr>
          </thead>
          <tbody>
            {t.lines.map((l) => (
              <tr key={l.id}>
                <td>
                  <Link to={`/lots/${l.lotId}`} className="font-medium hover:underline">
                    {l.itemNo}
                  </Link>
                  <div className="max-w-64 truncate text-xs text-slate-500">{l.itemName}</div>
                </td>
                <td>{l.colorName}</td>
                <td>{l.condition === 'N' ? 'New' : 'Used'}</td>
                <td className="text-right tabular-nums">{number(l.qty)}</td>
                <td className="text-right tabular-nums">{number(l.qtyShipped)}</td>
                <td className="text-right">
                  {t.status === 'in_transit' && writable ? (
                    <Input aria-label={`Arrived ${l.itemNo}`} inputMode="numeric" className="w-20 text-right" value={got[l.id] ?? String(l.qtyShipped)} onChange={(e) => setGot({ ...got, [l.id]: e.target.value })} />
                  ) : (
                    <span className="tabular-nums">{t.status === 'received' ? number(l.qtyReceived) : '—'}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ErrorMessage error={act.error} />
        {writable && (
          <div className="mt-4 flex flex-wrap gap-2">
            {t.status === 'pending' && (
              <Button
                disabled={act.isPending}
                onClick={async () => {
                  if (await confirm(`Send ${t.number}? The pieces leave ${t.from} and are held in transit, not available to sell, until received at ${t.to}.`, { label: 'Send it' })) act.mutate({ path: 'ship', body: { on: today() } });
                }}
              >
                Send
              </Button>
            )}
            {t.status === 'in_transit' && (
              <Button
                disabled={act.isPending}
                onClick={async () => {
                  const short = t.lines.some((l) => Number(got[l.id] ?? l.qtyShipped) < l.qtyShipped);
                  if (await confirm(short ? 'Some pieces did not arrive. They are written off as lost. Receive anyway?' : `Receive ${t.number} into ${t.to}?`, { label: 'Receive', danger: short })) {
                    act.mutate({ path: 'receive', body: { on: today(), lines: t.lines.map((l) => ({ lineId: l.id, qtyReceived: Number(got[l.id] ?? l.qtyShipped) })) } });
                  }
                }}
              >
                Receive
              </Button>
            )}
            {(t.status === 'pending' || t.status === 'in_transit') && (
              <Button
                variant="secondary"
                disabled={act.isPending}
                onClick={async () => {
                  if (await confirm(t.status === 'in_transit' ? `Cancel ${t.number}? The pieces go back to ${t.from}.` : `Cancel ${t.number}?`, { label: 'Cancel transfer', danger: true })) act.mutate({ path: 'cancel' });
                }}
              >
                Cancel transfer
              </Button>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
